/**
 * @name Login
 * @description Login para iniciar sesión en Talentry
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import {
  AuthService,
  EmployeeService,
  InstructorCourseService,
  PasswordService,
  SnackbarService,
} from '@services';

import { Footer, Lines } from '@components';
import { Environment } from '@config';
import { MUI, Typography } from '@styles';

import LoginForm from './components/login-form';
import ForgotPasswordDialog from './components/forgot-password.dialog';

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExternal: false,
      isOpenDialog: false,
    };

    this.values = {
      userName: '',
      password: '',
      keepSession: false,
      useRefreshTokens: true
    };

    this.schema = Yup.object({
      userName: Yup.string('').required('*'),
      password: Yup.string('').required('*')
    });
  }

  componentDidMount() {
    this.addObserver();
  }

  addObserver = () => {
    // NOTA: Se utiliza MutationObserver para detectar cambios en el atributo del input de tipo password para evitar que la contraseña sea desenmascarada.
    let target = document.getElementById('password');
    if (!target) {
      window.setTimeout(this.addObserver, 500);
      return;
    }

    // Create an observer instance.
    let observer = new MutationObserver((mutation) => {
      if (mutation[0].target.type !== 'password') {
        mutation[0].target.type = 'password';
      }
    });

    // Configuration of the observer:
    let config = {
      attributes: true // This is to watch for attribute changes.
    };

    // Pass in the element you wanna watch as well as the options.
    observer.observe(target, config);

    // Later, you can stop observing.
    // observer.disconnect();
  }

  openDialog = () => {
    this.setState({ isOpenDialog: true });
  }

  closeDialog = () => {
    this.setState({ isOpenDialog: false });
  }

  // Inicia sesión
  login = (data, setSubmitting) => {
    let { isExternal } = this.state;
    let goToPath = '';

    AuthService.login(data)
      .then(() => {
        isExternal = new RegExp(/\w+\.Talentry.\w+/).test(data.userName);
        this.setState({ isExternal });

        if (isExternal) {
          // Usuario externo
          EmployeeService.getInfoExternal(data.userName)
            .then((response) => {
              this.redirectExternalUser(response);
            });
        } else {
          // Usuario interno
          EmployeeService.getInfo()
            .then((response) => {
              const URLToRedirect = localStorage.getItem('URLToRedirect');

              if (URLToRedirect) {
                goToPath = URLToRedirect;
                localStorage.removeItem('URLToRedirect');
              } else {
                goToPath = '/home';
              }

              // SnackbarService.info(<Trans>GenericMessages.welcome</Trans>);
              this.setUserInfo(response);
              this.goHome(goToPath);
            })
            .catch((error) => {
              setSubmitting(false);
              let errorMsg = (!error) ? i18n.t('HttpResponse.error') : error.response.data;
              SnackbarService.error(errorMsg);
            });
        }
      })
      .catch((error) => {
        let errorMsg = (!error) ? i18n.t('GenericMessages.errorContactAdmin') : error.response.data.error_description;

        // Si el usuario esta bloqueado, enviar correo para desbloquear
        if (errorMsg === 'Usuario Bloqueado, favor de contactarte con el administrador.') {
          errorMsg = i18n.t('GenericMessages.userBlockedContactAdmin');
          PasswordService.unlock({ Usuario: data.userName });
        }

        setSubmitting(false);
        SnackbarService.error(errorMsg);
      });
  };

  // Guarda información del usuario en LocalStorage
  setUserInfo = (userInfo) => {
    localStorage.setItem('ls.infoUsuario', JSON.stringify(userInfo));
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    localStorage.setItem('ls.ty-Language', JSON.stringify({ language: userInfo.Empleado.Idioma }));

    // Switch Language
    i18n.use(initReactI18next).init({ lng: userInfo.Empleado.Idioma });
  };

  // Redirecciona a Inicio
  goHome = (path) => {
    const { history } = this.props;
    history.push(path);
  };

  // Redirecciona a un usuario externo
  redirectExternalUser = (externalUserInfo) => {
    const { isExternal } = this.state;
    let goToPath = '';
    let userInfo = {
      Aplicacion: [],
      Empresa: [],
      Parametro: [],
      Permiso: [],
      Rol: [],
      EsExterno: isExternal,
      Empleado: {
        Idioma: 'es',
        Foto: externalUserInfo.Foto,
        Nombre: externalUserInfo.Nombre
      },
    };

    InstructorCourseService.get()
      .then((response) => {
        if (response.length > 0) {
          userInfo.Aplicacion.push({
            IdAplicacion: 10,
            Aplicacion: 'Academy',
            Descripcion: '',
            Logo: 'https://assets.talentry.io/icons/appicons/academy.png',
            Estatus: true
          });
          goToPath = '';
          this.setUserInfo(userInfo);
          let URL = Environment.getCurrent().domain;
          window.location.href = `${URL.substring(0, URL.length - 2)}academy/#!/trainer-course`;
        }
      })
      .catch(() => {
        goToPath = '/bienvenida/externo';
        this.setUserInfo(userInfo);
        this.goHome(goToPath);
      });
  }

  // Modal de 'Recuperar contraseña'
  forgotPassword = () => {
    this.setState({ isOpenDialog: true });
  };

  render() {
    // Set Language Default (ES)
    if (!JSON.parse(localStorage.getItem('ls.ty-Language'))) {
      localStorage.setItem('ls.ty-Language', JSON.stringify({ language: 'es' }));
    }

    return (
      <>
        <Styled.LoginWrapper>
          <Styled.TopLines>
            <Lines
              direction="horizontal"
              firstLine={{ size: 75, color: 'blueMedium' }}
              secondLine={{ size: 25, color: 'red' }}
            />
          </Styled.TopLines>

          <Styled.FlexContent>
            <MUI.Grid container>
              <MUI.Grid item sm={6} xs={12}>
                <Styled.FlexRow>
                  <Styled.FlexLogo>
                    <Styled.LogoTalentry
                      src="https://assets.talentry.io/logo/logo-talentry.png"
                      alt="Talentry"
                    />
                  </Styled.FlexLogo>
                  <Styled.Divider orientation="vertical" />
                </Styled.FlexRow>
              </MUI.Grid>

              <MUI.Grid item sm={6} xs={12}>
                <Styled.FlexForm>
                  <MUI.Box display="flex" alignItems="center" height="20%">
                    <Typography.H4>
                      {i18n.t('Login.sessionStart')}
                    </Typography.H4>
                  </MUI.Box>
                  <MUI.Box display="flex" alignItems="center" height="60%">
                    <Formik
                      validateOnMount
                      validationSchema={this.schema}
                      initialValues={this.values}
                      initialErrors={this.values}
                      initialTouched={this.values}
                      onSubmit={(values, { setSubmitting }) => {
                        this.login(values, setSubmitting);
                      }}
                    >
                      {(formikProps) => (
                        <LoginForm {...formikProps} />
                      )}
                    </Formik>
                  </MUI.Box>
                  <MUI.Box display="flex" alignItems="center" justifyContent="center" height="20%">
                    <MUI.ButtonLink onClick={this.forgotPassword}>
                      {i18n.t('Login.forgotPassword')}
                    </MUI.ButtonLink>
                  </MUI.Box>
                </Styled.FlexForm>
              </MUI.Grid>
            </MUI.Grid>
          </Styled.FlexContent>

          <Footer />

          {/* Forgot Password Dialog */}
          {this.state.isOpenDialog ? (
            <ForgotPasswordDialog
              open={this.state.isOpenDialog}
              onClose={this.closeDialog}
            />
          ) : null}
        </Styled.LoginWrapper>
      </>
    );
  }
}

Login.propTypes = {
  // History router.
  history: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.LoginWrapper = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

Styled.FlexContent = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin-bottom: 64px;

  @media (max-width: 600px) {
    margin-bottom: 0px;
  }
`;

Styled.FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
`;

Styled.FlexLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 48px 72px;
  margin-left: 100px;
  margin-right: 8px;

  @media (max-width: 940px) {
    padding: 48px;
    margin: 0px;
  }
`;

Styled.FlexForm = styled.div`
  display: flex;
  flex-direction: column;
  height: 450px;
  padding: 48px 72px;
  margin-left: 8px;
  margin-right: 100px;

  @media (max-width: 940px) {
    padding: 48px;
    margin: 0px;
  }
`;

Styled.TopLines = styled.div`
  padding-top: 60px;
`;

Styled.LogoTalentry = styled.img`
  width: 100%;
`;

Styled.Divider = styled(MUI.Divider)`
  height: initial;

  @media (max-width: 600px) {
    display: none;
  }
`;

export default Login;
